/**
 * Dual-backend SQLite wrapper.
 *
 * Native (iOS / Android): uses @capacitor-community/sqlite for real SQLite.
 * Web: uses a localStorage-backed shim that implements just enough of the
 *      SQLite API to satisfy the app's queries. Avoids the jeep-sqlite WASM
 *      pipeline entirely (notorious for breaking on pnpm + Vite). Data
 *      volumes here are tiny (thousands of rows, not millions) so a pure-JS
 *      store is appropriate.
 *
 * The web shim recognizes the exact SQL statements used by the app. Adding a
 * new query requires adding a matching handler in this file.
 */

import { CapacitorSQLite, SQLiteConnection, type SQLiteDBConnection } from "@capacitor-community/sqlite";
import { Capacitor } from "@capacitor/core";

const DB_NAME = "cdl_prep";
const DB_VERSION = 1;
const WEB_STORAGE_KEY = "cdl:db";

const SCHEMA = `
CREATE TABLE IF NOT EXISTS packs (
  state_code TEXT NOT NULL, version INTEGER NOT NULL,
  published_at TEXT NOT NULL, downloaded_at TEXT NOT NULL,
  PRIMARY KEY (state_code, version)
);
CREATE TABLE IF NOT EXISTS sections (
  id TEXT PRIMARY KEY, state_code TEXT NOT NULL, pack_version INTEGER NOT NULL,
  test_type TEXT NOT NULL, ord INTEGER NOT NULL, title TEXT NOT NULL, manual_content_md TEXT NOT NULL
);
CREATE TABLE IF NOT EXISTS questions (
  id TEXT PRIMARY KEY, state_code TEXT NOT NULL, pack_version INTEGER NOT NULL,
  section_id TEXT NOT NULL, test_type TEXT NOT NULL, stem TEXT NOT NULL,
  options TEXT NOT NULL, correct_index INTEGER NOT NULL,
  explanation TEXT NOT NULL, source_citation TEXT NOT NULL
);
CREATE INDEX IF NOT EXISTS idx_questions_section ON questions(section_id);
CREATE INDEX IF NOT EXISTS idx_sections_state ON sections(state_code, pack_version);
CREATE TABLE IF NOT EXISTS pending_events (
  id INTEGER PRIMARY KEY AUTOINCREMENT, type TEXT NOT NULL,
  payload TEXT NOT NULL, created_at TEXT NOT NULL
);
CREATE TABLE IF NOT EXISTS local_progress (
  question_id TEXT PRIMARY KEY, answered_correct INTEGER NOT NULL, last_seen_at TEXT NOT NULL
);
`;

/**
 * Shape exposed to callers. Matches the subset of SQLiteDBConnection we use.
 */
export interface DbLike {
  run(statement: string, values?: unknown[]): Promise<{ changes: { changes: number } }>;
  query(statement: string, values?: unknown[]): Promise<{ values?: Record<string, unknown>[] }>;
  /**
   * Batch-execute multiple statements in a single transaction. Massively faster than
   * sequential run() calls — pack downloads (200+ INSERTs) drop from 30+ s to under 1 s
   * because the SQLite engine only fsyncs once. Apple's iPad reviewer (iOS 26.5) hung
   * indefinitely on the per-row INSERT loop before this was wired up.
   */
  executeSet(
    set: Array<{ statement: string; values?: unknown[] }>
  ): Promise<{ changes: { changes: number } }>;
  close?(): Promise<void>;
}

// ---------- Web shim (localStorage-backed) ----------

interface WebStore {
  packs: Record<string, unknown>[];
  sections: Record<string, unknown>[];
  questions: Record<string, unknown>[];
  pending_events: Record<string, unknown>[];
  local_progress: Record<string, unknown>[];
  pending_events_seq: number;
}

function emptyWebStore(): WebStore {
  return {
    packs: [],
    sections: [],
    questions: [],
    pending_events: [],
    local_progress: [],
    pending_events_seq: 0
  };
}

function loadWebStore(): WebStore {
  try {
    const raw = localStorage.getItem(WEB_STORAGE_KEY);
    if (!raw) return emptyWebStore();
    const parsed = JSON.parse(raw) as WebStore;
    return {
      packs: parsed.packs ?? [],
      sections: parsed.sections ?? [],
      questions: parsed.questions ?? [],
      pending_events: parsed.pending_events ?? [],
      local_progress: parsed.local_progress ?? [],
      pending_events_seq: parsed.pending_events_seq ?? 0
    };
  } catch {
    return emptyWebStore();
  }
}

function saveWebStore(store: WebStore): void {
  localStorage.setItem(WEB_STORAGE_KEY, JSON.stringify(store));
}

function createWebDb(): DbLike {
  const store = loadWebStore();

  function match(stmt: string, pattern: RegExp): boolean {
    return pattern.test(stmt.trim());
  }

  return {
    async run(statement, values = []) {
      const s = statement.trim();
      const v = values as unknown[];

      // INSERT OR REPLACE INTO packs
      if (match(s, /^INSERT OR REPLACE INTO packs/i)) {
        const [state_code, version, published_at, downloaded_at] = v as [string, number, string, string];
        const idx = store.packs.findIndex((r) => r.state_code === state_code && r.version === version);
        const row = { state_code, version, published_at, downloaded_at };
        if (idx >= 0) store.packs[idx] = row;
        else store.packs.push(row);
        saveWebStore(store);
        return { changes: { changes: 1 } };
      }

      // DELETE FROM sections WHERE state_code = ? AND pack_version = ?
      if (match(s, /^DELETE FROM sections WHERE state_code = \? AND pack_version = \?/i)) {
        const [state_code, pack_version] = v as [string, number];
        const before = store.sections.length;
        store.sections = store.sections.filter((r) => !(r.state_code === state_code && r.pack_version === pack_version));
        saveWebStore(store);
        return { changes: { changes: before - store.sections.length } };
      }

      // INSERT INTO sections
      if (match(s, /^INSERT INTO sections/i)) {
        const [id, state_code, pack_version, test_type, ord, title, manual_content_md] = v as [string, string, number, string, number, string, string];
        store.sections.push({ id, state_code, pack_version, test_type, ord, title, manual_content_md });
        saveWebStore(store);
        return { changes: { changes: 1 } };
      }

      // DELETE FROM questions WHERE state_code = ? AND pack_version = ?
      if (match(s, /^DELETE FROM questions WHERE state_code = \? AND pack_version = \?/i)) {
        const [state_code, pack_version] = v as [string, number];
        const before = store.questions.length;
        store.questions = store.questions.filter((r) => !(r.state_code === state_code && r.pack_version === pack_version));
        saveWebStore(store);
        return { changes: { changes: before - store.questions.length } };
      }

      // INSERT INTO questions
      if (match(s, /^INSERT INTO questions/i)) {
        const [id, state_code, pack_version, section_id, test_type, stem, options, correct_index, explanation, source_citation] = v as [string, string, number, string, string, string, string, number, string, string];
        store.questions.push({ id, state_code, pack_version, section_id, test_type, stem, options, correct_index, explanation, source_citation });
        saveWebStore(store);
        return { changes: { changes: 1 } };
      }

      // INSERT OR REPLACE INTO local_progress
      if (match(s, /^INSERT OR REPLACE INTO local_progress/i)) {
        const [question_id, answered_correct, last_seen_at] = v as [string, number, string];
        const idx = store.local_progress.findIndex((r) => r.question_id === question_id);
        const row = { question_id, answered_correct, last_seen_at };
        if (idx >= 0) store.local_progress[idx] = row;
        else store.local_progress.push(row);
        saveWebStore(store);
        return { changes: { changes: 1 } };
      }

      // INSERT INTO pending_events
      if (match(s, /^INSERT INTO pending_events/i)) {
        const [type, payload, created_at] = v as [string, string, string];
        store.pending_events_seq += 1;
        store.pending_events.push({ id: store.pending_events_seq, type, payload, created_at });
        saveWebStore(store);
        return { changes: { changes: 1 } };
      }

      // DELETE FROM pending_events WHERE id IN (?, ?, ...)
      if (match(s, /^DELETE FROM pending_events WHERE id IN/i)) {
        const idSet = new Set(v as number[]);
        const before = store.pending_events.length;
        store.pending_events = store.pending_events.filter((r) => !idSet.has(r.id as number));
        saveWebStore(store);
        return { changes: { changes: before - store.pending_events.length } };
      }

      console.warn("webDb.run: unhandled statement", s);
      return { changes: { changes: 0 } };
    },

    async query(statement, values = []) {
      const s = statement.trim();
      const v = values as unknown[];

      // Packs lookup
      if (match(s, /^SELECT state_code, version, published_at FROM packs WHERE state_code = \? ORDER BY version DESC LIMIT 1/i)) {
        const [state_code] = v as [string];
        const sorted = store.packs
          .filter((r) => r.state_code === state_code)
          .sort((a, b) => (b.version as number) - (a.version as number));
        return { values: sorted.slice(0, 1) };
      }

      // Sections by pack
      if (match(s, /^SELECT \* FROM sections WHERE state_code = \? AND pack_version = \? ORDER BY ord ASC/i)) {
        const [state_code, pack_version] = v as [string, number];
        const rows = store.sections
          .filter((r) => r.state_code === state_code && r.pack_version === pack_version)
          .sort((a, b) => (a.ord as number) - (b.ord as number));
        return { values: rows };
      }

      // Questions by pack
      if (match(s, /^SELECT \* FROM questions WHERE state_code = \? AND pack_version = \?$/i)) {
        const [state_code, pack_version] = v as [string, number];
        return {
          values: store.questions.filter((r) => r.state_code === state_code && r.pack_version === pack_version)
        };
      }

      // Questions by section
      if (match(s, /^SELECT \* FROM questions WHERE section_id = \?$/i)) {
        const [section_id] = v as [string];
        return { values: store.questions.filter((r) => r.section_id === section_id) };
      }

      // Questions by state + testType
      if (match(s, /^SELECT \* FROM questions WHERE state_code = \? AND test_type = \?$/i)) {
        const [state_code, test_type] = v as [string, string];
        return {
          values: store.questions.filter((r) => r.state_code === state_code && r.test_type === test_type)
        };
      }

      // Weak-area JOIN: questions INNER JOIN local_progress
      if (match(s, /^SELECT q\.\* FROM questions q\s+INNER JOIN local_progress p/i)) {
        const [state_code, limit] = v as [string, number];
        const wrongIds = new Set(
          store.local_progress.filter((p) => p.answered_correct === 0).map((p) => p.question_id)
        );
        const questions = store.questions.filter(
          (q) => q.state_code === state_code && wrongIds.has(q.id)
        );
        // Sort by most-recently-missed
        const lastSeenByQid = new Map(
          store.local_progress.map((p) => [p.question_id as string, p.last_seen_at as string])
        );
        questions.sort((a, b) =>
          (lastSeenByQid.get(b.id as string) ?? "").localeCompare(lastSeenByQid.get(a.id as string) ?? "")
        );
        return { values: questions.slice(0, limit) };
      }

      // All local_progress, no WHERE
      if (match(s, /^SELECT question_id, answered_correct FROM local_progress$/i)) {
        return { values: store.local_progress.map((r) => ({ question_id: r.question_id, answered_correct: r.answered_correct })) };
      }

      // Full local_progress with answered_correct only
      if (match(s, /^SELECT answered_correct FROM local_progress$/i)) {
        return { values: store.local_progress.map((r) => ({ answered_correct: r.answered_correct })) };
      }

      // local_progress with IN clause (HomeView section progress)
      if (match(s, /^SELECT answered_correct FROM local_progress WHERE question_id IN/i)) {
        const idSet = new Set(v as string[]);
        return {
          values: store.local_progress
            .filter((r) => idSet.has(r.question_id as string))
            .map((r) => ({ answered_correct: r.answered_correct }))
        };
      }

      // Question test_type lookup by id IN (...)
      if (match(s, /^SELECT id, test_type FROM questions WHERE id IN/i)) {
        const idSet = new Set(v as string[]);
        return {
          values: store.questions
            .filter((r) => idSet.has(r.id as string))
            .map((r) => ({ id: r.id, test_type: r.test_type }))
        };
      }

      // Pending events for sync
      if (match(s, /^SELECT id, type, payload, created_at FROM pending_events ORDER BY id ASC LIMIT \?/i)) {
        const [limit] = v as [number];
        const sorted = [...store.pending_events].sort((a, b) => (a.id as number) - (b.id as number));
        return { values: sorted.slice(0, limit) };
      }

      console.warn("webDb.query: unhandled statement", s);
      return { values: [] };
    },

    async executeSet(set) {
      // Web shim has no real transaction — localStorage is synchronous and
      // the dataset is small. Loop through and call run() for each statement;
      // each individual write persists. On native this is a single SQLite
      // transaction, which is where the speedup actually matters.
      let total = 0;
      for (const item of set) {
        const r = await this.run(item.statement, item.values);
        total += r.changes.changes;
      }
      return { changes: { changes: total } };
    },

    async close() {
      // no-op on web
    }
  };
}

// ---------- Native (real SQLite) ----------

let nativeConnection: SQLiteDBConnection | null = null;

async function initNativeDb(): Promise<DbLike> {
  const sqlite = new SQLiteConnection(CapacitorSQLite);

  // The Capacitor SQLite plugin keeps a process-wide map of named
  // connections. Across app restarts on iOS (and Xcode hot reloads in
  // the simulator), the native plugin can keep "cdl_prep" registered
  // even though our JS module-level `connection` is null. isConnection
  // proved unreliable here — it reported false while createConnection
  // still threw "Connection cdl_prep already exists" (caught in the
  // iPhone 17 Pro / iOS 26 simulator on 2026-05-21, same error Apple's
  // reviewer screenshotted).
  //
  // Defensive pattern: always closeConnection first (ignoring errors —
  // it's a no-op if nothing was registered), then createConnection
  // fresh. This guarantees the plugin's internal map and our usable
  // connection are in sync, with no reliance on the leaky probe.
  try {
    await sqlite.closeConnection(DB_NAME, false);
  } catch {
    // No prior connection — fine, continue to create.
  }
  nativeConnection = await sqlite.createConnection(
    DB_NAME, false, "no-encryption", DB_VERSION, false
  );

  // open() throws "already open" on a retrieved-but-still-open connection.
  // Probe first; only open when needed.
  const isOpen = await nativeConnection.isDBOpen();
  if (!isOpen.result) await nativeConnection.open();

  // In Capacitor 5 (and earlier versions of this plugin) execute() on a
  // SQLiteDBConnection took { statements: string }. In Capacitor 7 / plugin
  // 7.0.3, the signature changed to a positional string — passing the old
  // object form throws "Execute: Must provide raw SQL statements." Beta
  // testers never tripped this because they had pre-existing schema from
  // the Capacitor 5 build; every fresh install since the v5 → v7 upgrade
  // crashed here. Pass SCHEMA directly.
  const runner = nativeConnection as unknown as {
    execute: (statements: string, transaction?: boolean, isSQL92?: boolean) => Promise<unknown>;
  };
  await runner.execute(SCHEMA);

  // Wrap the native connection so executeSet explicitly passes transaction: true.
  // Different versions of @capacitor-community/sqlite have inconsistent defaults
  // for the transaction flag, and the entire point of executeSet for us is the
  // single-transaction speedup. Pass-through everything else by shape.
  const nc = nativeConnection as unknown as {
    run: DbLike["run"];
    query: DbLike["query"];
    executeSet: (set: Array<{ statement: string; values?: unknown[] }>, transaction: boolean) => Promise<{ changes: { changes: number } }>;
    close: () => Promise<void>;
  };
  return {
    run: (statement, values) => nc.run(statement, values),
    query: (statement, values) => nc.query(statement, values),
    executeSet: (set) => nc.executeSet(set, true),
    close: () => nc.close()
  };
}

// ---------- Public API ----------

let connection: DbLike | null = null;
let initPromise: Promise<void> | null = null;

export function initDb(): Promise<void> {
  if (connection) return Promise.resolve();
  if (initPromise) return initPromise;

  // Coalesce concurrent callers. main.ts, the readiness store, and the
  // pack store all hit initDb()/getDb() at startup; without this gate two
  // of them could both reach createConnection and trip the "already
  // exists" path through a race instead of a retry. Note: clear the
  // module-level handle via .then on the captured promise — not a
  // try/finally inside the IIFE — because an async body that hits no
  // awaits resolves synchronously and would clear `initPromise` before
  // the outer assignment completes (the bug that broke the web tests).
  const p = (async () => {
    if (Capacitor.isNativePlatform()) {
      connection = await initNativeDb();
    } else {
      connection = createWebDb();
    }
  })();
  initPromise = p;
  const clear = () => { if (initPromise === p) initPromise = null; };
  p.then(clear, clear);
  return p;
}

export async function getDb(): Promise<DbLike> {
  if (!connection) await initDb();
  return connection!;
}

export async function closeDb(): Promise<void> {
  if (nativeConnection) {
    await nativeConnection.close();
    nativeConnection = null;
  }
  connection = null;
  initPromise = null;
}
